/**
 * useCourseDetail Hook
 *
 * Fetches details for a single course (by slug) including enrollment status.
 * Exposes optimistic-update helpers used by CourseDetailPage.
 */

import { useState, useEffect, useCallback } from 'react';
import { getCourseDetail } from '../../services/coursesService';
import type { Course, MyEnrollment } from '../../types/courses.types';

interface UseCourseDetailResult {
  isLoading: boolean;
  error: string | null;
  course: Course | null;
  myEnrollment: MyEnrollment | null;
  refresh: () => void;
  updateEnrollment: (enrollment: MyEnrollment | null) => void;
  updateEnrolledCount: (delta: number) => void;
}

export function useCourseDetail(
  employeeId: number | null,
  slug: string | undefined,
  isAuthenticated: boolean
): UseCourseDetailResult {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [course, setCourse] = useState<Course | null>(null);
  const [myEnrollment, setMyEnrollment] = useState<MyEnrollment | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!employeeId || !slug || !isAuthenticated) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await getCourseDetail(slug);
      const { myEnrollment: enrollment, ...courseFields } = result;
      setCourse(courseFields as Course);
      setMyEnrollment(enrollment);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Der opstod en fejl');
    } finally {
      setIsLoading(false);
    }
  }, [employeeId, slug, isAuthenticated]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const updateEnrollment = useCallback((enrollment: MyEnrollment | null) => {
    setMyEnrollment(enrollment);
  }, []);

  const updateEnrolledCount = useCallback((delta: number) => {
    setCourse((prev) =>
      prev ? { ...prev, enrolledCount: Math.max(0, prev.enrolledCount + delta) } : prev
    );
  }, []);

  return {
    isLoading,
    error,
    course,
    myEnrollment,
    refresh: fetchDetail,
    updateEnrollment,
    updateEnrolledCount,
  };
}
